import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { useOnUpdateOnly } from '../../../hooks/UseOnUpdateOnly';

import type {
  IGetAdsRequestRange,
  ISearchPageFilter,
} from '../../../api/types/searchPageApiTypes';
import type { ParsedUrlQuery } from 'querystring';
import type { FilterType } from '../../../components/DealerPage/hooks/useFilters';

import {
  getQueryParams,
  mapFilterAndRangeValues,
} from '../../../helpers/seo/searchParams';
import {
  createHiddenFilters,
  findFilterByName,
} from '../../../components/DealerPage/mappers/filters';
import {
  ResponseMakeModelFilterValue,
  ResponseModelFilter,
} from '../../../types';
import { showroomFilters } from '../queries/showroomFilters';
import { PAGE } from '../../../helpers/pages';

type UseDisplayFiltersArgs = {
  section: string;
  setFilterAndRangeValues: (
    filtersData: ISearchPageFilter[],
    filtersQueryParams: FilterType[],
    rangeQueryParams?: IGetAdsRequestRange[],
  ) => void;
  userId: string | string[] | undefined;
  dealerSlug: string;
  queryParams: ParsedUrlQuery;
  populateMakeModelFilterWithOptions: (
    filtersData: ResponseMakeModelFilterValue,
    selectedMakeModelValues: ResponseModelFilter | null,
  ) => void;
};

function useDisplayFilters(args: UseDisplayFiltersArgs) {
  const {
    section,
    userId,
    setFilterAndRangeValues,
    queryParams,
    populateMakeModelFilterWithOptions,
    dealerSlug,
  } = args;

  const {
    query: { section: sectionQueryParam },
  } = useRouter();

  const { data } = useQuery({
    ...showroomFilters(section, dealerSlug),
    meta: {
      tags: [PAGE.DEALER_DETAILS],
      errorMessage: 'Error fetching dealer showroom sections',
      noToast: true,
    },
  });

  const filtersData = useMemo(
    () => [...(data?.showByDefault ?? []), ...createHiddenFilters()],
    [data?.showByDefault],
  );

  useOnUpdateOnly(() => {
    const makeModelFilter = findFilterByName(filtersData, 'makeModel');

    const [
      _dealerSlug,
      _sectionName,
      _makeParam,
      _modelParam,
      makeModelFilterParam,
    ] = Array.isArray(sectionQueryParam) ? sectionQueryParam : [];

    const makeModelFilterQuery = makeModelFilterParam
      ? getQueryParams(filtersData, makeModelFilterParam)
      : undefined;

    const { filterGroup, rangeGroup } = mapFilterAndRangeValues(
      { ...queryParams, ...makeModelFilterQuery },
      filtersData,
      userId,
    );

    // Set selected options if any
    setFilterAndRangeValues(filtersData, filterGroup, rangeGroup);

    if (
      makeModelFilter &&
      Array.isArray(makeModelFilter.values) &&
      makeModelFilter.values.length
    ) {
      const [values] = makeModelFilter.values;
      const { all, popular, valueType } = values;
      if (all && popular) {
        populateMakeModelFilterWithOptions({ all, popular, valueType }, null);
      }
    }
  }, [data?.showByDefault]);

  return {
    filtersData,
  };
}

export { useDisplayFilters };
